import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import * as React from 'react';
import { Transition as Transition$1 } from 'react-transition-group';
import useForkRef from '@mui/utils/useForkRef';
import { reflow } from '../utils/index.js';
import { animationClassesGenerator } from './utils.js';

var _excluded = ["appear", "children", "classes", "in", "onEnter", "onEntered", "onEntering", "onExit", "onExited", "onExiting", "style", "TransitionComponent"];
var Transition = function Transition(props, ref) {
  var appear = props.appear,
    children = props.children,
    classes = props.classes,
    inProp = props["in"],
    onEnter = props.onEnter,
    onEntered = props.onEntered,
    onEntering = props.onEntering,
    onExit = props.onExit,
    onExited = props.onExited,
    onExiting = props.onExiting,
    style = props.style,
    _props$TransitionComp = props.TransitionComponent,
    TransitionComponent = _props$TransitionComp === void 0 ? Transition$1 : _props$TransitionComp,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  var nodeRef = React.useRef(null);
  // @ts-ignore
  var foreignRef = useForkRef(children.ref, ref);
  var handleRef = useForkRef(nodeRef, foreignRef);
  var getNodeAnimationClasses = React.useMemo(function () {
    return animationClassesGenerator(classes);
  }, [classes]);
  var normalizedTransitionCallback = function normalizedTransitionCallback(callback) {
    return function (maybeIsAppearing) {
      if (callback) {
        var node = nodeRef.current;
        if (maybeIsAppearing === undefined) {
          callback(node);
        } else {
          callback(node, maybeIsAppearing);
        }
      }
    };
  };
  var handleEnter = normalizedTransitionCallback(function (node, isAppearing) {
    var _node$classList;
    reflow(node); // So the animation always start from the start.
    var _getNodeAnimationClas = getNodeAnimationClasses('enter'),
      classesToAdd = _getNodeAnimationClas.classesToAdd;
    (_node$classList = node.classList).add.apply(_node$classList, classesToAdd);
    if (onEnter) {
      onEnter(node, isAppearing);
    }
  });
  var handleEntering = normalizedTransitionCallback(function (node, isAppearing) {
    var _node$classList2, _node$classList3;
    var _getNodeAnimationClas2 = getNodeAnimationClasses('entering'),
      classesToAdd = _getNodeAnimationClas2.classesToAdd,
      classesToRemove = _getNodeAnimationClas2.classesToRemove;
    (_node$classList2 = node.classList).remove.apply(_node$classList2, classesToRemove);
    (_node$classList3 = node.classList).add.apply(_node$classList3, classesToAdd);
    if (onEntering) {
      onEntering(node, isAppearing);
    }
  });
  var handleEntered = normalizedTransitionCallback(function (node, isAppearing) {
    var _node$classList4;
    var _getNodeAnimationClas3 = getNodeAnimationClasses('entered'),
      classesToRemove = _getNodeAnimationClas3.classesToRemove;
    (_node$classList4 = node.classList).remove.apply(_node$classList4, classesToRemove);
    if (onEntered) {
      onEntered(node, isAppearing);
    }
  });
  var handleExit = normalizedTransitionCallback(function (node) {
    var _node$classList5;
    var _getNodeAnimationClas4 = getNodeAnimationClasses('exit'),
      classesToAdd = _getNodeAnimationClas4.classesToAdd;
    (_node$classList5 = node.classList).add.apply(_node$classList5, classesToAdd);
    if (onExit) {
      onExit(node);
    }
  });
  var handleExiting = normalizedTransitionCallback(function (node) {
    var _node$classList6, _node$classList7;
    var _getNodeAnimationClas5 = getNodeAnimationClasses('exiting'),
      classesToAdd = _getNodeAnimationClas5.classesToAdd,
      classesToRemove = _getNodeAnimationClas5.classesToRemove;
    (_node$classList6 = node.classList).remove.apply(_node$classList6, classesToRemove);
    (_node$classList7 = node.classList).add.apply(_node$classList7, classesToAdd);
    if (onExiting) {
      onExiting(node);
    }
  });
  var handleExited = normalizedTransitionCallback(function (node) {
    var _node$classList8;
    var _getNodeAnimationClas6 = getNodeAnimationClasses('entering'),
      classesToRemove = _getNodeAnimationClas6.classesToRemove;
    (_node$classList8 = node.classList).remove.apply(_node$classList8, classesToRemove);
    if (onExited) {
      onExited(node);
    }
  });
  return /*#__PURE__*/React.createElement(TransitionComponent, _extends({
    appear: appear,
    "in": inProp,
    nodeRef: nodeRef,
    onEnter: handleEnter,
    onEntered: handleEntered,
    onEntering: handleEntering,
    onExit: handleExit,
    onExited: handleExited,
    onExiting: handleExiting
  }, other), function (state, childProps) {
    return /*#__PURE__*/React.cloneElement(children, _extends({
      ref: handleRef,
      style: _extends({
        visibility: state === 'exited' && !inProp ? 'hidden' : undefined
      }, style, children.props.style)
    }, childProps));
  });
};
var RefForwardedTransition = /*#__PURE__*/React.forwardRef(Transition);

export { RefForwardedTransition as default };
//# sourceMappingURL=index.js.map
